/**
 * @param {Object} props
 * @param {any} props.value - The raw value to display.
 * @param {string} [props.formattedValue] - Optional pre-formatted value from parent.
 * @param {'date' | 'phone' | 'decimal' | 'code'} [props.enumType] - The formatting type.
 * @param {string} [props.dateFormat] - Optional date format (used with enumType='date').
 * @param {object} [props.color] - Optional color settings for label and value.
 * @param {any} [props.*] - Additional TextField props.
 */

import React from 'react';
import TextField from '@mui/material/TextField';
import { formatDate } from 'src/utils/date-locale'; // Make sure this is implemented
import { formatDisplayByPattern } from 'src/utils/formatters';

const ReadOnlyTextField = ({
  value,
  formattedValue,
  enumType,
  dateFormat,
  color = { label: 'text.secondary', value: 'inherit' },
  ...otherProps
}) => {
  const formatValue = React.useCallback((val) => {
    if (val === null || val === undefined || val === '') return '';

    if (dateFormat) {
      return formatDate(val, dateFormat);
    }

    switch (enumType) {
      case 'phone':
        return formatDisplayByPattern(val, [4, 3, 3], ' ');
        // return val.toString().replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
      case 'decimal':
        return Number(val).toLocaleString();
      case 'code':
        const cleaned = val.toString().replace(/\s+/g, '');
        return cleaned.replace(/(.{1,4})(?=(.{4})+$)/g, '$& ');
      default:
        return val;
    }
  }, [enumType, dateFormat]);

  const displayValue = React.useMemo(() => {
    return formattedValue ?? formatValue(value);
  }, [formattedValue, value, formatValue]);

  return (
    <TextField
      {...otherProps}
      fullWidth
      variant="outlined"
      value={displayValue}
      InputLabelProps={{
        sx: { color: color?.label },
      }}
      InputProps={{
        readOnly: true,
        sx: {
          color: color?.value,
          cursor: 'text',
          userSelect: 'text',
        },
      }}
      tabIndex={-1}
      style={{
        pointerEvents: 'auto',
        userSelect: 'text',
      }}
    />
  );
};

export default ReadOnlyTextField;
